/**
 * usePreference — read + write a single per-user preference, backed by
 * GET / PUT /api/v1/me/preferences.
 *
 * Backs the BON-64 density + dark-mode toggles. Optimistic update on
 * mutate so the UI flips instantly; rolls back on error.
 *
 * Usage:
 *   const { value, set, isLoading } = usePreference('dashboards.density', 'comfortable');
 *   <button onClick={() => set('compact')}>Compact</button>
 *
 * **Important — value safety.** The backend accepts `z.unknown()` for the
 * `value` field on PUT, so the only thing guaranteed is "valid JSON".
 * The TypeScript `T` you pass here is treated as a trust assertion, not
 * a runtime check — if the server returns a value with a different shape
 * than `T` (e.g., a key was set with a different type before, or via curl),
 * you get a silent type lie. If a preference becomes safety-critical
 * (e.g., admin-only feature flag), add a per-key gate in
 * `backend/src/routes/me-preferences.routes.ts` and/or runtime-validate
 * here at the call site.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../../../lib/apiFetch';

const PREFERENCES_QUERY_KEY = ['me', 'preferences'];

type PrefMap = Record<string, unknown>;

async function fetchAll(): Promise<PrefMap> {
  const res = await apiFetch('/api/v1/me/preferences');
  if (!res.ok) throw new Error(`GET /me/preferences ${res.status}`);
  return (await res.json()) as PrefMap;
}

async function putOne(key: string, value: unknown): Promise<{ key: string; value: unknown }> {
  const res = await apiFetch('/api/v1/me/preferences', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, value }),
  });
  if (!res.ok) throw new Error(`PUT /me/preferences ${res.status}`);
  return (await res.json()) as { key: string; value: unknown };
}

export function usePreference<T>(
  key: string,
  defaultValue: T,
): {
  value: T;
  set: (next: T) => void;
  isLoading: boolean;
  error: Error | null;
} {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: PREFERENCES_QUERY_KEY, queryFn: fetchAll });

  const mutation = useMutation({
    mutationFn: (next: T) => putOne(key, next),
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: PREFERENCES_QUERY_KEY });
      const previous = qc.getQueryData<PrefMap>(PREFERENCES_QUERY_KEY);
      qc.setQueryData<PrefMap>(PREFERENCES_QUERY_KEY, {
        ...(previous ?? {}),
        [key]: next,
      });
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.previous) qc.setQueryData(PREFERENCES_QUERY_KEY, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: PREFERENCES_QUERY_KEY }),
  });

  const fromServer = query.data?.[key];
  const value = fromServer === undefined ? defaultValue : (fromServer as T);

  return {
    value,
    set: (next: T) => mutation.mutate(next),
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}
